/**
 * PostHog Analytics Module
 * Central export for all analytics functionality
 */

export { posthog, PostHogProvider } from './posthog';
export { reportWebVitals } from './webVitals';

// Event tracking modules
export { authEvents } from './events/auth';
export { enrollmentEvents } from './events/enrollment';
export { paymentEvents } from './events/payment';
export { builderEvents } from './events/builder';
export { certificateEvents } from './events/certificate';

// Types
export type * from './types';

// Re-export posthog for direct access
import { posthog } from './posthog';
import { authEvents } from './events/auth';
import { enrollmentEvents } from './events/enrollment';
import { paymentEvents } from './events/payment';
import { builderEvents } from './events/builder';
import { certificateEvents } from './events/certificate';
import type { UserTraits } from './types';

/**
 * Main analytics object providing access to all tracking methods
 */
export const analytics = {
  auth: authEvents,
  enrollment: enrollmentEvents,
  payment: paymentEvents,
  builder: builderEvents,
  certificate: certificateEvents,

  /**
   * Identify a user with their traits
   */
  identify: (userId: string, traits?: UserTraits) => {
    if (!posthog.__loaded) return;
    posthog.identify(userId, traits);
  },

  /**
   * Reset user identity (on logout)
   */
  reset: () => {
    if (!posthog.__loaded) return;
    posthog.reset();
  },

  /**
   * Get a feature flag value
   */
  getFeatureFlag: (flag: string): boolean | string | undefined => {
    if (!posthog.__loaded) return undefined;
    return posthog.getFeatureFlag(flag);
  },

  /**
   * Check if a feature flag is enabled
   */
  isFeatureEnabled: (flag: string): boolean => {
    if (!posthog.__loaded) return false;
    return posthog.isFeatureEnabled(flag) ?? false;
  },

  /**
   * Capture a custom event directly
   */
  capture: (eventName: string, properties?: Record<string, unknown>) => {
    if (!posthog.__loaded) return;
    posthog.capture(eventName, {
      ...properties,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Set user properties without capturing an event
   */
  setPersonProperties: (properties: Record<string, unknown>) => {
    if (!posthog.__loaded) return;
    posthog.setPersonProperties(properties);
  },

  /**
   * Track page view manually
   */
  pageView: (pageName?: string, properties?: Record<string, unknown>) => {
    if (!posthog.__loaded) return;
    posthog.capture('$pageview', {
      $current_url: typeof window !== 'undefined' ? window.location.href : undefined,
      page_name: pageName,
      ...properties,
    });
  },
};

export default analytics;
